import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message, DatePicker, Select, Input, Form } from "antd";
import dayjs from "dayjs";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";

import { getUsers } from "../../api/user";
import { getClients } from "../../api/client";
import { createProject, getProject, updateProject } from "../../api/project";
import { getContacts } from "../../api/contact";

import { ProjectStatus } from "../../lib/constants";
import ProjectEmployees from "../../components/project/ProjectEmployees";
import ProjectEquipments from "../../components/project/ProjectEquipments";
import ProjectTasks from "../../components/project/ProjectTasks";

export default function ProjectForm() {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [searchUser, setSearchUser] = useState([]);
  const [searchClient, setSearchClient] = useState([]);
  const [searchContacts, setSearchContacts] = useState([]);
  const [project, setProject] = useState(null);
  const [clientId, setClientId] = useState(null);

  const populateSearch = async () => {
    const users = await getUsers({});
    const clients = await getClients({});
    setSearchUser(users.results);
    setSearchClient(clients.results);
  };

  const fetchProject = useCallback(async () => {
    const data = await getProject(projectId);

    // Ensure the client exists in searchClient, otherwise add it manually
    if (!searchClient.find((client) => client.id === data.client.id)) {
      setSearchClient((prev) => [
        ...prev,
        { id: data.client.id, name: data.client.name },
      ]);
    }

    // Ensure the lead exists in searchUser, otherwise add it manually
    if (!searchUser.find((user) => user.id === data.lead.id)) {
      setSearchUser((prev) => [
        ...prev,
        { id: data.lead.id, firstName: data.lead.firstName },
      ]);
    }

    // Ensure the contact exists in searchContacts, otherwise add it manually
    if (data.contact && !searchContacts.find((contact) => contact.id === data.contact.id)) {
      setSearchContacts((prev) => [
        ...prev,
        { id: data.contact.id, firstName: data.contact.firstName, lastName: data.contact.lastName },
      ]);
    }

    const values = {
      name: data.name,
      description: data.description,
      leadId: data.lead.id,
      clientId: data.client.id,
      status: data.status,
      startAt: dayjs(data.startAt),
      endAt: dayjs(data.endAt),
      contactId: data.contact?.id,
      // plannedStartAt: dayjs(data.plannedStartAt),
      // plannedEndAt: dayjs(data.plannedEndAt),
    };
    form.setFieldsValue(values);
    setClientId(data.client.id);
    setProject(data);
  }, [form, projectId]);

  useEffect(() => {
    populateSearch();
    if (projectId) {
      setEditing(true);
      fetchProject();
    }
  }, [fetchProject, projectId]);

  useEffect(() => {
    async function fetchClientContacts() {
      const { results } = await getContacts({ clientId: clientId });
      setSearchContacts(results);
    }

    if (clientId) {
      fetchClientContacts();
    }
  }, [clientId, form]);

  const clientSearchOptions = useMemo(() => {
    return searchClient.map((client) => {
      return {
        value: client.id,
        label: client.name,
      };
    });
  }, [searchClient]);

  const searchOptions = useMemo(() => {
    return searchUser.map((user) => {
      return {
        value: user.id,
        label: user.firstName + " " + (user.lastName || ""),
      };
    });
  }, [searchUser]);

  const contactSearchOptions = useMemo(() => {
    return searchContacts?.map((contact) => {
      return {
        value: contact.id,
        label: contact.firstName + " " + (contact.lastName || ""),
      };
    });
  }, [searchContacts]);

  const handleSubmit = async ({
    name,
    description,
    leadId,
    clientId,
    status,
    startAt,
    endAt,
    contactId,
    // plannedStartAt,
    // plannedEndAt,
  }) => {
    const projectData = {
      name,
      description,
      lead: { id: leadId },
      client: clientId ? { id: clientId } : undefined,
      contact: { id: contactId },
      status,
      startAt,
      endAt,
      // plannedStartAt,
      // plannedEndAt,
    };

    try {
      if (editing) {
        await updateProject({ ...project, ...projectData, id: projectId });
        navigate("/projects/" + projectId);
      } else {
        const res = await createProject(projectData);
        if (res) {
          navigate("/projects/" + res.id);
        }
      }
      message.success(
        editing
          ? "Project updated successfully"
          : "Project created successfully"
      );
    } catch (error) {
      message.error("Something went wrong. Please try again!");
    }
  };

  return (
    <main className="space-y-6 min-h-full flex flex-col">
      <PageHeader
        title={editing ? "Update Project" : "Create Project"}
        home="Projects"
        route="/projects"
      />

      <div className="bg-white ml-6 p-6 ">
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="grid grid-cols-2 gap-6 w-1/2">
            <Form.Item
              name={"name"}
              label="Project Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name={"leadId"}
              label="Project Lead"
              rules={[{ required: true }]}
            >
              <Select
                placeholder={"Search to Select"}
                showSearch
                options={searchOptions}
                rules={[{ required: true }]}
                defaultActiveFirstOption={false}
                filterOption={false}
                mode="tag"
                onSearch={async (str) => {
                  const { results: users } = await getUsers({ q: str });
                  setSearchUser(users);
                }}
              />
            </Form.Item>
            <Form.Item
              name={"clientId"}
              label="Client"
              rules={[{ required: true }]}
            >
              <Select
                placeholder={"Search to Select"}
                showSearch
                options={clientSearchOptions}
                onSearch={async (str) => {
                  const { results: clients } = await getClients({ q: str });
                  setSearchClient(clients);
                }}
                onSelect={(value) => {
                  if (value) {
                    setClientId(value);
                  }
                }}
                rules={[{ required: true }]}
                defaultActiveFirstOption={false}
                filterOption={false}
                mode="tag"
              />
            </Form.Item>
            <Form.Item name={"contactId"} label="Contact" required>
              <Select
                placeholder={"Select Contact"}
                showSearch
                options={contactSearchOptions}
                onSearch={async (str) => {
                  const { results: contacts } = await getContacts({
                    q: str,
                    clientId: clientId,
                  });
                  setSearchContacts(contacts);
                }}
                mode="tag"
                filterOption={false}
              />
            </Form.Item>
            <Form.Item
              name={"status"}
              label="Status"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select a option and change input text above"
                defaultActiveFirstOption
                options={Object.keys(ProjectStatus).map((status) => {
                  return {
                    value: ProjectStatus[status],
                    label: status,
                  };
                })}
              />
            </Form.Item>
            {/* <Form.Item
              name={"plannedStartAt"}
              label="Planned Start Date"
            >
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              name={"plannedEndAt"}
              label="Planned Due Date"
            >
              <DatePicker className="w-full" />
            </Form.Item> */}
            <Form.Item
              name={"startAt"}
              label="Project Start Date"
              rules={[{ required: true }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              name={"endAt"}
              label="Project Complete Date"
              rules={[{ required: true }]}
            >
              <DatePicker className="w-full" />
            </Form.Item>
            <Form.Item
              className="col-span-2"
              name={"description"}
              label="Project Description"
              rules={[]}
            >
              <Input.TextArea />
            </Form.Item>
            {/* <Form.Item
                            name={"attachments"}
                            label="Attachments"
                            rules={[

                            ]}
                        >
                            <Input />
                        </Form.Item> */}
          </div>
          <BlueButton type="primary" htmlType="submit">
            {editing ? "Update " : "Create "}
            Project
          </BlueButton>
        </Form>
      </div>

      <section className=" ml-6 grid grid-cols-2 space-x-6 ">
        <ProjectEmployees project={project} afterClose={fetchProject} />

        <ProjectEquipments project={project} afterClose={fetchProject} />
      </section>

      <section className=" ml-6 ">
        <ProjectTasks project={project} afterClose={fetchProject} />
      </section>
    </main>
  );
}
